import React, { useContext, useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import _ from 'lodash';
import { type RestSimpleListLaCard } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { useDetectIE } from '@bbkAdminUtils/detect-ie';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { OtherActions } from '@bbkFunnel/components/modals/sections/other-actions';
import { FunnelButton } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnel/utils/refs.context';
import { isSVGLink } from '@bbkFunnel/utils/shared';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { LossGridItem } from '@bbkFunnelComponents/loss/sections/grid-item';
import { LossTitle } from '@bbkFunnelComponents/loss/sections/title';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import Icon from '@bbkFunnelUtils/exit-icon';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

import './loss-simple-list.scss';

type Props = {
  card: RestSimpleListLaCard;
  onClick?: () => void;
  handleLossCardAction?: ({ action }: { action: RestModalForwards }) => void;
  isHorizontal: boolean;
};

type BulletIconPicker = React.ComponentProps<typeof ActionButtonIcons>['icon'];

const ImageContainer = styled.div<{
  isHorizontal: boolean;
  isRegular: boolean;
}>`
  width: 100%;
  max-width: ${({ isRegular, isHorizontal }) =>
    isRegular ? '180px' : isHorizontal ? '500px' : '100%'};
  max-height: ${({ isRegular, isHorizontal }) =>
    isRegular ? '180px' : isHorizontal ? '500px' : '100%'};

  display: flex;
  align-items: center;
`;

const LaSimpleList = styled.div<{ cols: number; isHorizontal: boolean }>`
  display: grid;
  font-size: 0.88em;
  padding-top: 10px;
  line-height: 22px;
  grid-template-columns: ${({ isHorizontal, cols }) =>
    isHorizontal ? '25% 25% 25% 25%' : cols === 2 ? '50% 50%' : '100%'};
`;

const LaSimpleListItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 5px 5px;
`;

const Image = styled.img`
  width: 100%;
`;

const SvgImage = styled.div<{ image: string }>`
  backgroundimage: ${({ image }) => `url(${image})`};
`;

const BulletIcon = styled.img`
  width: 16px;
  height: auto;
  margin-top: -11px;
  margin-right: 0.5rem;
`;

const BulletIconContainer = styled.div`
  padding-top: 4px;
`;

const ButtonContainer = styled.div``;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ContentContainer = styled.div<{ isHorizontal: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ isHorizontal }) => (isHorizontal ? 'padding: 30px;' : '')}
  justify-content: space-between;
`;

const OuterContainer = styled.div<{ isHorizontal: boolean }>`
  display: flex;
  flex-direction: ${({ isHorizontal }) => (isHorizontal ? 'row' : 'column')};
`;

const LossSimpleList: React.FC<Props> = ({
  card,
  card: {
    id,
    max_redemptions_reached,
    requires: {
      items,
      title,
      cols = 2,
      image,
      bullet_icon,
      bullet_icon_picker,
      forwards,
      other_actions,
    },
  },
  onClick,
  handleLossCardAction,
  isHorizontal,
}) => {
  const theme = useTheme() as EmotionTheme;
  const { isPreview } = useContext(FunnelContext);
  const isIE = useDetectIE();
  const isVertical = !isHorizontal && !!image;
  const isRegular = !isHorizontal && !isVertical;

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const btnRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn`, btnRef), [btnRef]);

  const specificStyles = {
    backgroundColor: theme.buttonBackgroundColor,
    width: '100%',
  };
  const fillColor = _.get(card, 'style_overlay.fillColor', theme.primaryColor);

  const bulletIconFromPicker =
    bullet_icon_picker && bullet_icon_picker !== 'none'
      ? (bullet_icon_picker as BulletIconPicker)
      : undefined;

  const renderBulletIcon = () => {
    if (bullet_icon) {
      return <BulletIcon src={bullet_icon} alt="" />;
    }
    if (bulletIconFromPicker) {
      return (
        <span css={{ marginTop: -4, marginLeft: -3, display: 'inline-block' }}>
          <ActionButtonIcons icon={bulletIconFromPicker} />
        </span>
      );
    }
    return <Icon name="close" classes="list-close-icon" />;
  };

  return (
    <LossGridItem
      classes={cs('bbk-funnel-loss-simple-list', makeClassNameSafe(card.name))}
      onClick={isPreview ? onClick : () => undefined}
      isHorizontal={isHorizontal}
    >
      <OuterContainer isHorizontal={isHorizontal}>
        {image && !isRegular && (
          <ImageContainer
            isHorizontal={isHorizontal}
            isRegular={isRegular}
            ref={imageRef}
          >
            {isSVGLink(image) && !isIE ? (
              <SvgImage
                image={image}
                className="bkk-image-svg bkk-sl-sc-image-svg"
              />
            ) : (
              <Image srcSet={`${image} 2x`} alt="" />
            )}
          </ImageContainer>
        )}

        <ContentContainer isHorizontal={isHorizontal}>
          <ListContainer>
            <LossTitle>
              <span ref={titleRef}>
                <CustomerHtml value={title} />
              </span>
            </LossTitle>
            <LaSimpleList isHorizontal={isHorizontal} cols={cols}>
              {items.map((item, index) => (
                <LaSimpleListItem key={index}>
                  <BulletIconContainer
                    style={{ fill: fillColor, color: fillColor }}
                  >
                    {renderBulletIcon()}
                  </BulletIconContainer>
                  <span className="text">
                    <CustomerHtml value={item} />
                  </span>
                </LaSimpleListItem>
              ))}
            </LaSimpleList>
          </ListContainer>
          <ButtonContainer>
            {forwards && (
              <div
                className={cs(
                  'btn',
                  'd-flex align-items-center justify-content-center',
                  'mt-3'
                )}
                ref={btnRef}
              >
                <FunnelButton
                  disabled={max_redemptions_reached}
                  onClick={() => {
                    handleLossCardAction?.({ action: forwards });
                  }}
                  specificStyles={specificStyles}
                >
                  {forwards?.icon && (
                    <ActionButtonIcons
                      styles={{ marginRight: 4 }}
                      icon={forwards.icon}
                    />
                  )}
                  {forwards.text}
                </FunnelButton>
              </div>
            )}
            {other_actions && (
              <OtherActions
                version2Template
                id={id}
                otherActions={other_actions}
                onClick={(action) => handleLossCardAction?.({ action })}
              />
            )}
          </ButtonContainer>
        </ContentContainer>
      </OuterContainer>
    </LossGridItem>
  );
};

export default LossSimpleList;
